// How long the curtain motor runs, per direction. Both shells.
//
// Two numbers, 1-20 seconds, stored per output on the server — see
// core/devices/curtain.js for where and why. An installer sets these once by
// timing the curtain with a stopwatch; nobody comes back to this screen.
//
// ⚠️ THE TWO DIRECTIONS ARE SEPARATE ON PURPOSE. A heavy curtain climbing
// against its own weight does not take the same time coming back, and one
// shared number means one direction always over- or under-runs.

import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box,
  Typography, Alert, Slider,
} from '@mui/material';
import {
  MIN_TRAVEL_S, MAX_TRAVEL_S, DEFAULT_TRAVEL_S, clampTravel,
} from '../../core/devices/curtain';
import { FIT_XS } from './fitDialog';

const marks = [
  { value: MIN_TRAVEL_S, label: `${MIN_TRAVEL_S}s` },
  { value: 10, label: '10s' },
  { value: MAX_TRAVEL_S, label: `${MAX_TRAVEL_S}s` },
];

function TravelField({ label, value, onChange }) {
  return (
    <Box sx={{ mb: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 96 }}>{label}</Typography>
        <TextField
          size="small"
          type="number"
          value={value}
          // ⚠️ NOT CLAMPED WHILE TYPING. Clamping on every keystroke makes "12"
          // impossible to type when the minimum is 1 — the 1 becomes 1 and the 2
          // lands after it only by luck. The value is clamped on save and on
          // blur, which is the only place it matters.
          onChange={(e) => onChange(e.target.value)}
          onBlur={() => onChange(clampTravel(value))}
          inputProps={{ min: MIN_TRAVEL_S, max: MAX_TRAVEL_S, style: { width: 56 } }}
        />
        <Typography variant="body2" color="text.secondary">seconds</Typography>
      </Box>
      <Slider
        size="small"
        value={Number(value) || DEFAULT_TRAVEL_S}
        min={MIN_TRAVEL_S}
        max={MAX_TRAVEL_S}
        step={1}
        marks={marks}
        valueLabelDisplay="auto"
        onChange={(e, v) => onChange(v)}
        sx={{ mt: 0.5 }}
      />
    </Box>
  );
}

export default function CurtainTimesDialog({
  open, onClose, curtain, deviceName, reversed = false,
}) {
  const [openS, setOpenS] = useState(DEFAULT_TRAVEL_S);
  const [closeS, setCloseS] = useState(DEFAULT_TRAVEL_S);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Reload from the device every time it opens: another phone may have changed
  // them, and a stale form would write the old numbers back.
  useEffect(() => {
    if (!open) return;
    setOpenS(curtain.times.openSeconds);
    setCloseS(curtain.times.closeSeconds);
    setError('');
  }, [open, curtain.times]);

  const save = async () => {
    setSaving(true);
    setError('');
    const out = await curtain.saveTimes({ openSeconds: openS, closeSeconds: closeS });
    setSaving(false);
    if (out.ok) onClose();
    else setError(out.error);
  };

  const unset = !curtain.times.openSet || !curtain.times.closeSet;
  const { singleOutput } = curtain.times;
  const noun = (curtain.noun || 'Curtain').toLowerCase();

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} {...FIT_XS}>
      <DialogTitle sx={{ pb: 0.5 }}>
        {`${curtain.noun || 'Curtain'} run time`}
        {deviceName && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {deviceName}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {`The motor is switched on for this long and then off again. Time the
          ${noun} from fully closed to fully open with a stopwatch and put that
          number in.`}
        </Typography>

        {/* ⚠️ ONE FIELD WHEN THERE IS ONLY ONE OUTPUT ROW TO STORE IT IN. The
            sliding gate is `Outputs 1` in the catalogue while its firmware
            drives two relays, so there is no Output 2 row — and a write to one
            that does not exist updates nothing and reports success. Offering a
            second field would let somebody set a close time that silently never
            saved. See core/devices/curtain.js. */}
        <TravelField
          label={singleOutput ? 'Run time' : 'Open'}
          value={openS}
          onChange={(v) => { setOpenS(v); if (singleOutput) setCloseS(v); }}
        />
        {!singleOutput && <TravelField label="Close" value={closeS} onChange={setCloseS} />}
        {singleOutput && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -0.5 }}>
            This device has a single output, so one time covers both directions.
          </Typography>
        )}

        {/* Say when a number is the fallback rather than somebody's choice —
            otherwise "3" reads as a decision that has already been made. */}
        {unset && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {`Not set yet, so ${DEFAULT_TRAVEL_S} seconds is being used — the same default the device itself uses.`}
          </Typography>
        )}

        {/* ⚠️ THE TIMES DO NOT FOLLOW THE REVERSE. The direction swap happens
            inside the device (core/devices/curtainReverse) and the user still
            presses Open, so the open seconds still belong to the open button —
            but an installer who had worked around the reversal by swapping
            these two numbers now has both corrections applied at once. */}
        {reversed && !singleOutput && (
          <Alert severity="warning" sx={{ mt: 1.5, py: 0.5 }}>
            <Typography variant="caption">
              {`Reversed ${noun}: Open above is still the Open button's time.`}
            </Typography>
          </Alert>
        )}

        {/* ⚠️ The honest limit. A relay curtain has its own 3-second ceiling
            that nothing can currently change, so a longer setting here simply
            will not be reached on that hardware. Better said than discovered. */}
        <Alert severity="info" sx={{ mt: 1.5, py: 0.5 }}>
          <Typography variant="caption">
            {`Some ${noun}s also stop themselves after about 3 seconds, and that
            limit is inside the device. Where it applies, the shorter of the two
            wins — this setting can bring a run in, never extend it.`}
          </Typography>
        </Alert>

        {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
